import { isValid, isValidIndex } from '../../utils/utilities';

export const updateAdjacentSeatNumbers = (dimensions, seatTypes, seatNumbers, seatOrientations) => {
    const newAdjacentSeatNumbers = {};
    
    // Check if all non-empty seats are sleepers
    const allSleepers = Object.values(seatTypes).every(type => type === 'empty' || type === 'sleeper');

    for (let row = 0; row < dimensions.rows; row++) {
        for (let col = 0; col < dimensions.cols; col++) {
            const seatKey = `${row}-${col}`;
            const seatType = seatTypes[seatKey];
            const seatNumber = seatNumbers[seatKey];
            const seatOrientation = seatOrientations && seatOrientations[seatKey];

            // Skip empty seats or seats without numbers
            if (seatType === 'empty' || !seatNumber) continue;

            let adjacentSeatNumber = null;
            
            // For landscape oriented seats, use row-based logic
            if (seatOrientation === 'landscape') {
                // Check previous row (row-based logic for landscape)
                if (row > 0) {
                    const prevRowKey = `${row - 1}-${col}`;
                    const prevRowSeatType = seatTypes[prevRowKey];
                    const prevRowSeatNumber = seatNumbers[prevRowKey];
                    const prevRowSeatOrientation = seatOrientations && seatOrientations[prevRowKey];

                    if (prevRowSeatType !== 'empty' && prevRowSeatNumber && prevRowSeatOrientation === 'landscape') {
                        adjacentSeatNumber = prevRowSeatNumber;
                    }
                }

                // If no adjacent found in previous row, check next row
                if (!adjacentSeatNumber && row < dimensions.rows - 1) {
                    const nextRowKey = `${row + 1}-${col}`;
                    const nextRowSeatType = seatTypes[nextRowKey];
                    const nextRowSeatNumber = seatNumbers[nextRowKey];
                    const nextRowSeatOrientation = seatOrientations && seatOrientations[nextRowKey];

                    if (nextRowSeatType !== 'empty' && nextRowSeatNumber && nextRowSeatOrientation === 'landscape') {
                        adjacentSeatNumber = nextRowSeatNumber;
                    }
                }
            } else {
                // Original column-based logic for portrait and other orientations
                // Special case for last row based on seat type
                const isLastRow = allSleepers ?
                    row === dimensions.rows - 1 : // If all seats are sleepers, use seater logic
                    (seatType === 'sleeper' ?
                        row === Math.floor(dimensions.rows / 2) - 1 : // For sleepers, last row is half of total rows
                        row === dimensions.rows - 1); // For seaters, last row is total rows - 1

                if (isLastRow) {
                    const prevRowKey = `${row - 1}-${col}`;
                    const prevRowSeatType = seatTypes[prevRowKey];
                    const prevRowColKey = `${row - 1}-${col - 1}`;
                    const prevRowColSeatType = seatTypes[prevRowColKey];
                    const prevRowColRightKey = `${row - 1}-${col + 1}`;
                    const prevRowColRightSeatType = seatTypes[prevRowColRightKey];

                    if (prevRowSeatType === 'empty' || (!isValid(prevRowColSeatType) && prevRowColRightSeatType === 'empty')) {
                        adjacentSeatNumber = seatNumber;
                    }
                    else if (prevRowColSeatType === 'empty') {
                        const leftSeatKey = `${row}-${col - 1}`;
                        const leftSeatType = seatTypes[leftSeatKey];
                        const rightSeatKey = `${row}-${col + 1}`;
                        const rightSeatType = seatTypes[rightSeatKey];
                        if (leftSeatType !== 'empty' && rightSeatType !== 'empty' && col > 0) {
                            const rightSeatKey = `${row}-${col + 1}`;
                            const rightSeatType = seatTypes[rightSeatKey];
                            const rightSeatNumber = seatNumbers[rightSeatKey];

                            if (rightSeatType !== 'empty') {
                                adjacentSeatNumber = rightSeatNumber;
                            }
                        }
                    }
                }

                // Check left seat if no adjacent number found
                if (!adjacentSeatNumber && col > 0) {
                    const leftSeatKey = `${row}-${col - 1}`;
                    const leftSeatType = seatTypes[leftSeatKey];
                    const leftSeatNumber = seatNumbers[leftSeatKey];

                    if (leftSeatType !== 'empty') {
                        adjacentSeatNumber = leftSeatNumber;
                    }
                }

                // Check right seat if no adjacent number found
                if (!adjacentSeatNumber && col < dimensions.cols - 1) {
                    const rightSeatKey = `${row}-${col + 1}`;
                    const rightSeatType = seatTypes[rightSeatKey];
                    const rightSeatNumber = seatNumbers[rightSeatKey];

                    if (rightSeatType !== 'empty') {
                        adjacentSeatNumber = rightSeatNumber;
                    }
                    else {
                        adjacentSeatNumber = seatNumber;
                    }
                }
            }

            if (adjacentSeatNumber) {
                newAdjacentSeatNumbers[seatKey] = adjacentSeatNumber;
            }
        }
    }

    return newAdjacentSeatNumbers;
};

const addDescriptionParams = (description, params) => {
    description = description.trim()
    if (params.length>0) {
        return (description+" "+params);
    }
    else {
        return description;
    }
}

export const getDescriptionName = (coach_type,isAc,seat_type,name) => {
    let description="";
    if(isValid(coach_type)){
        description = addDescriptionParams(description,coach_type.trim());
    }
    if(isValidIndex(isAc)){
        let acDesc=(isAc==1?"A/C":"Non-A/C");
        description = addDescriptionParams(description,acDesc.trim());
    }
    if(isValid(seat_type)){
        description = addDescriptionParams(description,seat_type.trim());
    }
    if(isValid(name)){
        description = addDescriptionParams(description,name.trim());
    }
    return description;
}
// Auto size for a toilet cell: sleeper when a horizontally adjacent seat is a
// sleeper, seater otherwise. The user can still override it from the seat editor.
export const getAutoToiletSize = (seatTypes, seatKey) => {
    const [row, col] = seatKey.split('-').map(Number);
    const neighborKeys = [`${row}-${col - 1}`, `${row}-${col + 1}`];
    return neighborKeys.some(key => seatTypes[key] === 'sleeper') ? 'sleeper' : 'seater';
}
// Auto size for an emergency exit placed on an empty cell. A column is drawn as
// one vertical strip on the booking layout, and a sleeper cell is twice the
// height of a seater one, so the cells above and below the exit decide its
// footprint; the seats alongside it are the fallback when its own column has
// nothing to go by (a rear exit under the last row of a short column).
export const getAutoExitSize = (seatTypes, seatKey) => {
    const [row, col] = seatKey.split('-').map(Number);
    const neighborKeys = [`${row - 1}-${col}`, `${row + 1}-${col}`, `${row}-${col - 1}`, `${row}-${col + 1}`];
    const neighborKey = neighborKeys.find(key => isValid(seatTypes[key]) && seatTypes[key] !== 'empty');
    return seatTypes[neighborKey] === 'sleeper' ? 'sleeper' : 'seater';
}
